import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Edit3,
  Loader2,
  BookOpen,
  TestTube2,
  Send,
  Eye,
  EyeOff,
  RefreshCw,
  ExternalLink,
  Info,
} from 'lucide-react';
import { apiClient, TribalKnowledgeSourceConfig } from '../api/client';
import { useTheme } from '../components/theme-provider';

// Import custom icons
import slackColorIcon from '../assets/icons/slack-color.png';
import { ReactComponent as ConfluenceIcon } from '../assets/icons/confluence.svg';
import { ReactComponent as JiraIcon } from '../assets/icons/jira.svg';
import { ReactComponent as GleanIcon } from '../assets/icons/glean.svg';
import NotionIconLight from '../assets/icons/notion-logo-no-background.png';
import NotionIconDark from '../assets/icons/notion-logo-white.png';
import LinearIconLight from '../assets/icons/linear.png';
import LinearIconDark from '../assets/icons/linear-dark-mode.png';

// Auth field types
type AuthField = 'api_key' | 'api_token' | 'base_url' | 'workspace_id';

// Icon type can be SVG component or PNG image with light/dark variants
type IconConfig = 
  | { type: 'svg'; light: React.ComponentType<{ className?: string }>; dark: React.ComponentType<{ className?: string }> }
  | { type: 'svg-single'; component: React.ComponentType<{ className?: string }> }
  | { type: 'png'; light: string; dark: string };

// Setup instructions for each source type
interface SetupInstruction {
  steps: string[];
  docsUrl: string;
  docsLabel: string;
  notes?: string;
}

// Source type configurations with metadata
const SOURCE_TYPES: Record<string, {
  name: string;
  icon: IconConfig;
  description: string;
  authFields: AuthField[];
  optionalFields: AuthField[];
  setup: SetupInstruction;
  usesExistingIntegration?: boolean; // If true, uses existing Kestrel integration (e.g., Slack)
}> = {
  slack: {
    name: 'Slack',
    icon: { type: 'png', light: slackColorIcon, dark: slackColorIcon },
    description: 'Search Slack messages and threads for relevant discussions',
    authFields: [], // No auth fields needed - uses existing Kestrel Slack integration
    optionalFields: [],
    setup: {
      steps: [
        'Connect your Slack workspace via the Slack Integration page',
        'The Kestrel Slack app will be used for tribal knowledge search',
        'Invite @Kestrel to channels you want to be searchable',
      ],
      docsUrl: '/integrations/slack',
      docsLabel: 'Go to Slack Integration',
      notes: 'Slack tribal knowledge uses the same Kestrel app as incident notifications. No additional setup required if already connected.',
    },
    usesExistingIntegration: true, // Special flag for Slack
  },
  notion: {
    name: 'Notion',
    icon: { type: 'png', light: NotionIconLight, dark: NotionIconDark },
    description: 'Query Notion pages and documentation',
    authFields: ['api_key'],
    optionalFields: ['workspace_id'],
    setup: {
      steps: [
        'Go to notion.so/my-integrations',
        'Click "+ New integration"',
        'Name your integration (e.g., "Kestrel Tribal Knowledge")',
        'Select the workspace to associate with the integration',
        'Copy the "Internal Integration Secret" token',
        'Share the pages/databases you want searchable with your integration',
      ],
      docsUrl: 'https://developers.notion.com/docs/create-a-notion-integration',
      docsLabel: 'Notion Integration Guide',
      notes: 'You must explicitly share each page or database with the integration for it to be searchable.',
    },
  },
  confluence: {
    name: 'Confluence',
    icon: { type: 'svg-single', component: ConfluenceIcon },
    description: 'Search Confluence wiki pages and spaces',
    authFields: ['api_key', 'api_token', 'base_url'],
    optionalFields: [],
    setup: {
      steps: [
        'Go to id.atlassian.com/manage-profile/security/api-tokens',
        'Click "Create API token"',
        'Give it a label (e.g., "Kestrel Integration")',
        'Copy the generated API token',
        'Enter your Atlassian account email in the Email Address field',
        'Set Base URL to your Confluence instance (e.g., https://yourcompany.atlassian.net)',
      ],
      docsUrl: 'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/',
      docsLabel: 'Atlassian API Token Guide',
      notes: 'Authentication uses Basic Auth with your email and API token.',
    },
  },
  jira: {
    name: 'Jira',
    icon: { type: 'svg-single', component: JiraIcon },
    description: 'Find related issues and post-mortems in Jira',
    authFields: ['api_key', 'api_token', 'base_url'],
    optionalFields: [],
    setup: {
      steps: [
        'Go to id.atlassian.com/manage-profile/security/api-tokens',
        'Click "Create API token"',
        'Give it a label (e.g., "Kestrel Integration")',
        'Copy the generated API token',
        'Use your Atlassian account email as the API Key field',
        'Set Base URL to your Jira instance (e.g., https://yourcompany.atlassian.net)',
      ],
      docsUrl: 'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/',
      docsLabel: 'Atlassian API Token Guide',
      notes: 'Enter your email in the API Key field and the API token in the API Token field. Uses the same Atlassian API token as Confluence.',
    },
  },
  linear: {
    name: 'Linear',
    icon: { type: 'png', light: LinearIconLight, dark: LinearIconDark },
    description: 'Search Linear issues and projects',
    authFields: ['api_key'],
    optionalFields: [],
    setup: {
      steps: [
        'Go to linear.app/settings/account/security',
        'Click "New API Key" under Personal API keys',
        'Give your key a label (e.g., "Kestrel Integration")',
        'Copy the generated API key',
      ],
      docsUrl: 'https://developers.linear.app/docs/graphql/working-with-the-graphql-api#personal-api-keys',
      docsLabel: 'Linear API Documentation',
      notes: 'Personal API keys have the same permissions as your user account. Consider using a service account for production.',
    },
  },
  glean: {
    name: 'Glean',
    icon: { type: 'svg-single', component: GleanIcon },
    description: 'Enterprise search across all connected sources',
    authFields: ['api_key'],
    optionalFields: ['workspace_id'],
    setup: {
      steps: [
        'Contact your Glean administrator to enable API access',
        'Go to your Glean Admin Console → API → API Keys',
        'Click "Create API Key"',
        'Select appropriate scopes (search:read is required)',
        'Copy the generated API key',
      ],
      docsUrl: 'https://developers.glean.com/docs/api_overview',
      docsLabel: 'Glean API Documentation',
      notes: 'Glean API access requires an Enterprise plan. Contact your Glean account manager if you don\'t see the API option.',
    },
  },
};

type SourceType = keyof typeof SOURCE_TYPES;

// Icon component that handles different icon types
const SourceIcon: React.FC<{ sourceType: string; isDarkMode: boolean; className?: string }> = ({ 
  sourceType, 
  isDarkMode, 
  className = "h-5 w-5" 
}) => {
  const config = SOURCE_TYPES[sourceType as SourceType];
  if (!config) return <BookOpen className={className} />;
  
  const iconConfig = config.icon;
  
  if (iconConfig.type === 'svg') {
    const Icon = isDarkMode ? iconConfig.dark : iconConfig.light;
    return <Icon className={className} />;
  } else if (iconConfig.type === 'svg-single') {
    const Icon = iconConfig.component;
    return <Icon className={className} />;
  } else {
    // PNG images
    const src = isDarkMode ? iconConfig.dark : iconConfig.light;
    return <img src={src} alt={config.name} className={className} />;
  }
};

// Slack integration status type
interface SlackIntegrationStatus {
  connected: boolean;
  team_name?: string;
  team_id?: string;
}

const TribalKnowledgeSettings: React.FC = () => {
  const [sources, setSources] = useState<TribalKnowledgeSourceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { theme } = useTheme();
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Slack integration status (for reusing existing Kestrel Slack app)
  const [slackStatus, setSlackStatus] = useState<SlackIntegrationStatus | null>(null);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<TribalKnowledgeSourceConfig | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    source_type: '' as SourceType | '',
    name: '',
    api_key: '',
    api_token: '',
    workspace_id: '',
    base_url: '',
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Update dark mode state when theme changes
  useEffect(() => {
    const updateDarkMode = () => {
      if (theme === 'dark') {
        setIsDarkMode(true);
      } else if (theme === 'light') {
        setIsDarkMode(false);
      } else {
        // For 'system' theme, check the actual system preference
        setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    };

    updateDarkMode();

    // Listen for system theme changes when using 'system' theme
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => updateDarkMode();
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  // Fetch sources and Slack status on mount
  useEffect(() => {
    fetchSources();
    fetchSlackStatus();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.listTribalKnowledgeSources();
      const HIDDEN_TYPES = ['posthog', 'vercel', 'railway', 'flyio', 'nebius', 'beam', 'daytona', 'supabase', 'planetscale', 'neon', 'clickhouse', 'jenkins', 'circleci', 'sonarcloud', 'okta', 'databricks', 'vault', 'infisical'];
      setSources((response.sources || []).filter((s: any) => !HIDDEN_TYPES.includes(s.source_type)));
    } catch (err: any) {
      console.error('Failed to fetch tribal knowledge sources:', err);
      setError(err.response?.data?.message || 'Failed to load tribal knowledge sources');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlackStatus = async () => {
    try {
      const status = await apiClient.getSlackIntegrationStatus();
      setSlackStatus(status);
    } catch (err) {
      console.error('Failed to fetch Slack status:', err);
      // Don't show error - Slack status is optional
    }
  };

  const resetForm = () => {
    setFormData({
      source_type: '',
      name: '',
      api_key: '',
      api_token: '',
      workspace_id: '',
      base_url: '',
      enabled: true,
    });
    setShowApiKey(false);
  };

  const handleAddSource = async () => {
    if (!formData.source_type) {
      setError('Please select a source type');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      const response = await apiClient.createTribalKnowledgeSource({
        source_type: formData.source_type,
        name: formData.name || SOURCE_TYPES[formData.source_type as SourceType].name,
        api_key: formData.api_key || undefined,
        api_token: formData.api_token || undefined,
        workspace_id: formData.workspace_id || undefined,
        base_url: formData.base_url || undefined,
        enabled: formData.enabled,
      });
      
      if (response.source) {
        setSources([...sources, response.source]);
        setSuccess(`${SOURCE_TYPES[formData.source_type as SourceType].name} source added successfully`);
        setIsAddDialogOpen(false);
        resetForm();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      console.error('Failed to add source:', err);
      setError(err.response?.data?.message || 'Failed to add tribal knowledge source');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSource = async () => {
    if (!selectedSource?.id) return;

    setSaving(true);
    setError('');
    
    try {
      const response = await apiClient.updateTribalKnowledgeSource(selectedSource.id, {
        name: formData.name || undefined,
        api_key: formData.api_key || undefined,
        api_token: formData.api_token || undefined,
        workspace_id: formData.workspace_id || undefined,
        base_url: formData.base_url || undefined,
        enabled: formData.enabled,
      });
      
      if (response.source) {
        setSources(sources.map(s => s.id === selectedSource.id ? response.source : s));
        setSuccess('Source updated successfully');
        setIsEditDialogOpen(false);
        setSelectedSource(null);
        resetForm();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      console.error('Failed to update source:', err);
      setError(err.response?.data?.message || 'Failed to update tribal knowledge source');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSource = async () => {
    if (!selectedSource?.id) return;

    setSaving(true);
    setError('');
    
    try {
      const response = await apiClient.deleteTribalKnowledgeSource(selectedSource.id);
      
      if (response.success) {
        setSources(sources.filter(s => s.id !== selectedSource.id));
        setSuccess('Source deleted successfully');
        setIsDeleteDialogOpen(false);
        setSelectedSource(null);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err: any) {
      console.error('Failed to delete source:', err);
      setError(err.response?.data?.message || 'Failed to delete tribal knowledge source');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSource = async (source: TribalKnowledgeSourceConfig) => {
    if (!source.id) return;

    setTesting(source.id);
    setError('');
    setSuccess('');
    
    try {
      const response = await apiClient.testTribalKnowledgeSource(source.id);
      
      if (response.success) {
        setSuccess(`${source.name || source.source_type} connection successful!`);
      } else {
        setError(response.message || 'Connection test failed');
      }
      
      // Refresh sources to update connection status
      await fetchSources();
    } catch (err: any) {
      console.error('Failed to test source:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to test tribal knowledge source');
      // Still refresh to show the error status
      await fetchSources();
    } finally {
      setTesting(null);
      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    }
  };

  const openEditDialog = (source: TribalKnowledgeSourceConfig) => {
    setSelectedSource(source);
    setFormData({
      source_type: source.source_type as SourceType,
      name: source.name || '',
      api_key: '', // Don't show existing keys
      api_token: '',
      workspace_id: source.workspace_id || '',
      base_url: source.base_url || '',
      enabled: source.enabled,
    });
    setIsEditDialogOpen(true);
  };

  const getSourceIcon = (sourceType: string, className?: string) => {
    return <SourceIcon sourceType={sourceType} isDarkMode={isDarkMode} className={className || "h-5 w-5"} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={fetchSources}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
        <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Add Source
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="border-green-500/50 bg-green-500/10">
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertTitle className="text-green-600 dark:text-green-400">Success</AlertTitle>
          <AlertDescription className="text-green-600 dark:text-green-400">{success}</AlertDescription>
        </Alert>
      )}

      {/* Slack Suggestion Card - Show when Slack is connected but not enabled for tribal knowledge */}
      {slackStatus?.connected && !sources.some(s => s.source_type === 'slack') && (
        <Card className="border-border bg-muted/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getSourceIcon('slack')}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">Slack Available</h3>
                    <Badge variant="outline" className="text-xs">
                      Connected as {slackStatus.team_name}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your Slack workspace is already connected. Enable tribal knowledge search to find relevant discussions and threads during incident response.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => {
                  resetForm();
                  setFormData(prev => ({ ...prev, source_type: 'slack' }));
                  setIsAddDialogOpen(true);
                }}
                variant="default"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Enable Slack Search
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sources List */}
      <div className="space-y-4">
        {sources.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <h3 className="font-medium text-lg mb-2">No knowledge sources connected</h3>
              <p className="text-muted-foreground mb-4">
                Connect your organization's Slack, Confluence, Jira, Linear, or other knowledge sources
                to enhance incident response and enable workflow automations.
              </p>
              <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Source
              </Button>
            </CardContent>
          </Card>
        ) : (
          sources.map((source) => {
            const sourceConfig = SOURCE_TYPES[source.source_type as SourceType];
            return (
              <Card key={source.id} className={!source.enabled ? 'opacity-60' : ''}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getSourceIcon(source.source_type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{source.name || sourceConfig?.name || source.source_type}</h3>
                          <Badge variant="outline" className="text-xs capitalize">
                            {source.source_type}
                          </Badge>
                          {source.connection_status === 'connected' && (
                            <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-normal">
                              Connected
                            </Badge>
                          )}
                          {source.connection_status === 'error' && (
                            <Badge variant="destructive" className="text-xs">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Error
                            </Badge>
                          )}
                          {source.connection_status === 'pending' && (
                            <Badge variant="secondary" className="text-xs">
                              Pending Test
                            </Badge>
                          )}
                          {!source.enabled && (
                            <Badge variant="secondary" className="text-xs">
                              Disabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {sourceConfig?.description || 'Knowledge source'}
                        </p>
                        {source.connection_error && source.connection_status === 'error' && (
                          <p className="text-xs text-destructive mt-1">
                            Error: {source.connection_error}
                          </p>
                        )}
                        {source.base_url && (
                          <p className="text-xs text-muted-foreground mt-1">
                            URL: {source.base_url}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestSource(source)}
                        disabled={testing === source.id}
                      >
                        {testing === source.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-3 w-3" />
                        )}
                        <span className="ml-1.5">Test Connection</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(source)}
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => { setSelectedSource(source); setIsDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Add Source Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Knowledge Source</DialogTitle>
            <DialogDescription>
              Connect a new source of tribal knowledge to enhance incident response and enable workflow automations.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Source Type</Label>
              <Select
                value={formData.source_type}
                onValueChange={(value) => setFormData({ ...formData, source_type: value as SourceType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a source type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {getSourceIcon(key, "h-4 w-4")}
                        <span>{config.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.source_type && (
              <>
                {/* Special handling for Slack - uses existing Kestrel integration */}
                {formData.source_type === 'slack' ? (
                  <div className="space-y-4">
                    {slackStatus?.connected ? (
                      <>
                        {/* Slack is connected via Kestrel app */}
                        <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                            <CheckCircle className="h-4 w-4" />
                            Slack workspace connected
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Your Slack workspace <strong>{slackStatus.team_name}</strong> is already connected via the Kestrel Slack app. 
                            This same connection will be used for tribal knowledge search.
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Make sure to invite @Kestrel to channels you want to be searchable.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Display Name (optional)</Label>
                          <Input
                            placeholder="Slack"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Slack is not connected */}
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm font-medium text-amber-600 dark:text-amber-400">
                            <AlertCircle className="h-4 w-4" />
                            Slack workspace not connected
                          </div>
                          <p className="text-sm text-muted-foreground">
                            To use Slack for tribal knowledge search, you first need to connect your Slack workspace 
                            via the Slack Integration page. The same Kestrel app is used for both incident notifications 
                            and tribal knowledge search.
                          </p>
                          <Link
                            to="/integrations/slack"
                            className="inline-flex items-center gap-2 text-sm text-blue-500 hover:text-blue-600 hover:underline"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Go to Slack Integration
                          </Link>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Setup Instructions for other sources */}
                    <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Info className="h-4 w-4 text-blue-500" />
                        How to get your {SOURCE_TYPES[formData.source_type as SourceType]?.name} credentials
                      </div>
                      <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
                        {SOURCE_TYPES[formData.source_type as SourceType]?.setup.steps.map((step, index) => (
                          <li key={index} className="leading-relaxed">{step}</li>
                        ))}
                      </ol>
                      {SOURCE_TYPES[formData.source_type as SourceType]?.setup.notes && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded px-2 py-1.5">
                          <strong>Note:</strong> {SOURCE_TYPES[formData.source_type as SourceType]?.setup.notes}
                        </p>
                      )}
                      <a
                        href={SOURCE_TYPES[formData.source_type as SourceType]?.setup.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {SOURCE_TYPES[formData.source_type as SourceType]?.setup.docsLabel}
                      </a>
                    </div>

                    <div className="space-y-2">
                      <Label>Display Name (optional)</Label>
                      <Input
                        placeholder={SOURCE_TYPES[formData.source_type as SourceType]?.name}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

                    {SOURCE_TYPES[formData.source_type as SourceType]?.authFields.includes('api_key') && (
                      <div className="space-y-2">
                        <Label>
                          {formData.source_type === 'confluence' || formData.source_type === 'jira' 
                            ? 'Email Address' 
                            : 'API Key'}
                        </Label>
                        <div className="relative">
                          <Input
                            type={formData.source_type === 'confluence' || formData.source_type === 'jira' ? 'email' : (showApiKey ? 'text' : 'password')}
                            placeholder={formData.source_type === 'confluence' || formData.source_type === 'jira' 
                              ? 'your.email@company.com' 
                              : 'Enter API key'}
                            value={formData.api_key}
                            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                          />
                          {formData.source_type !== 'confluence' && formData.source_type !== 'jira' && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-0 top-0 h-full px-3"
                              onClick={() => setShowApiKey(!showApiKey)}
                            >
                              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {SOURCE_TYPES[formData.source_type as SourceType]?.authFields.includes('api_token') && (
                      <div className="space-y-2">
                        <Label>API Token</Label>
                        <div className="relative">
                          <Input
                            type={showApiKey ? 'text' : 'password'}
                            placeholder="Enter API token"
                            value={formData.api_token}
                            onChange={(e) => setFormData({ ...formData, api_token: e.target.value })}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3"
                            onClick={() => setShowApiKey(!showApiKey)}
                          >
                            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>
                    )}

                    {SOURCE_TYPES[formData.source_type as SourceType]?.authFields.includes('base_url') && (
                      <div className="space-y-2">
                        <Label>Base URL</Label>
                        <Input
                          placeholder="https://your-instance.atlassian.net"
                          value={formData.base_url}
                          onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                        />
                        <p className="text-xs text-muted-foreground">
                          Your Atlassian instance URL (e.g., https://yourcompany.atlassian.net)
                        </p>
                      </div>
                    )}

                    {SOURCE_TYPES[formData.source_type as SourceType]?.optionalFields.includes('workspace_id') && (
                      <div className="space-y-2">
                        <Label>Workspace ID (optional)</Label>
                        <Input
                          placeholder="Enter workspace ID"
                          value={formData.workspace_id}
                          onChange={(e) => setFormData({ ...formData, workspace_id: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            {formData.source_type === 'slack' && !slackStatus?.connected ? (
              <Link to="/integrations/slack">
                <Button>
                  Connect Slack First
                </Button>
              </Link>
            ) : (
              <Button 
                onClick={handleAddSource} 
                disabled={saving || !formData.source_type}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {formData.source_type === 'slack' ? 'Enable Slack Search' : 'Add Source'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Source Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {formData.source_type && getSourceIcon(formData.source_type, "h-5 w-5")}
              Edit {SOURCE_TYPES[formData.source_type as SourceType]?.name} Source
            </DialogTitle>
            <DialogDescription>
              Update the configuration for this knowledge source.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                placeholder={SOURCE_TYPES[formData.source_type as SourceType]?.name}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {formData.source_type && SOURCE_TYPES[formData.source_type as SourceType]?.authFields.includes('api_key') && (
              <div className="space-y-2">
                <Label>
                  {formData.source_type === 'confluence' || formData.source_type === 'jira' 
                    ? 'Email Address (leave blank to keep existing)' 
                    : 'API Key (leave blank to keep existing)'}
                </Label>
                <div className="relative">
                  <Input
                    type={formData.source_type === 'confluence' || formData.source_type === 'jira' ? 'email' : (showApiKey ? 'text' : 'password')}
                    placeholder={formData.source_type === 'confluence' || formData.source_type === 'jira' 
                      ? 'your.email@company.com' 
                      : 'Enter new API key'}
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                  />
                  {formData.source_type !== 'confluence' && formData.source_type !== 'jira' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {formData.source_type && SOURCE_TYPES[formData.source_type as SourceType]?.authFields.includes('api_token') && (
              <div className="space-y-2">
                <Label>API Token (leave blank to keep existing)</Label>
                <div className="relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    placeholder="Enter new API token"
                    value={formData.api_token}
                    onChange={(e) => setFormData({ ...formData, api_token: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {formData.source_type && SOURCE_TYPES[formData.source_type as SourceType]?.authFields.includes('base_url') && (
              <div className="space-y-2">
                <Label>Base URL</Label>
                <Input
                  placeholder="https://your-instance.atlassian.net"
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Your Atlassian instance URL (e.g., https://yourcompany.atlassian.net)
                </p>
              </div>
            )}

            {formData.source_type && SOURCE_TYPES[formData.source_type as SourceType]?.optionalFields.includes('workspace_id') && (
              <div className="space-y-2">
                <Label>Workspace ID</Label>
                <Input
                  placeholder="Enter workspace ID"
                  value={formData.workspace_id}
                  onChange={(e) => setFormData({ ...formData, workspace_id: e.target.value })}
                />
              </div>
            )}

            {/* Setup Instructions Link */}
            {formData.source_type && (
              <div className="pt-2 border-t">
                <a
                  href={SOURCE_TYPES[formData.source_type as SourceType]?.setup.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3 w-3" />
                  Need help? View {SOURCE_TYPES[formData.source_type as SourceType]?.setup.docsLabel}
                </a>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSource} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Knowledge Source?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the {selectedSource?.name || selectedSource?.source_type} knowledge source.
              Incident analysis will no longer include context from this source.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSource}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TribalKnowledgeSettings;

